import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import Input from "components/Input";
import PillButton from "components/button/Pills";
import { showToast } from "components/Toaster";
import httpClient from "services/network/httpClient";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { signOut } from "firebase/auth";
import { auth } from "../components/firebase/firebase";
import { useNavigate } from "react-router-dom";

interface UpdatePasswordFormProps {
  onClose: () => void;
}

const updatePasswordSchema = yup.object({
  oldPassword: yup.string().required("Old password is required"),
  newPassword: yup
    .string()
    .required("New password is required")
    .min(6, "Minimum 6 characters")
    .matches(/[A-Z]/, "At least one uppercase letter")
    .matches(/\d/, "At least one number")
    .matches(
      /[!@#$%^&*(),.?":{}|<>]/,
      "At least one special character"
    ),
  confirmPassword: yup
    .string()
    .required("Confirm your password")
    .oneOf([yup.ref("newPassword")], "Passwords do not match"),
});

const UpdatePasswordForm = ({ onClose }: UpdatePasswordFormProps) => {
  const methods = useForm({
    mode: "onChange",
    resolver: yupResolver(updatePasswordSchema),
  });

  const navigate = useNavigate();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      console.log("User logged out successfully!");

      // Clear local storage
      localStorage.removeItem("accessToken");

      // Redirect to login page
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const submit = (data: any) => {

    console.log("Submitting password update:", data);
    httpClient
      .post("/admin/update-password", {
        newPassword: data.newPassword,
        oldPassword: data.oldPassword,
      })
      .then((response: any) => {
        console.log("Full Password update response:", response);

        // Check if the API call was successful (200 status)
        // httpClient wraps successful responses with success: true
        if (response.success === true) {
          // If we got here, the HTTP call succeeded (200 status)
          console.log("Password updated successfully");
          showToast("Password updated successfully", "success");
          handleLogout();
          methods.reset();
          onClose();
        } else {
          // Extract error message from various possible locations
          let errorMessage = "Failed to update password";

          // Check response.value (when backend returns 200 with error in body)
          if (response.value?.message && typeof response.value.message === 'string') {
            errorMessage = response.value.message;
          } else if (response.value?.error?.message && typeof response.value.error.message === 'string') {
            errorMessage = response.value.error.message;
          }
          // Check response.error (when httpClient catches an error)
          else if (response.error?.message && typeof response.error.message === 'string') {
            errorMessage = response.error.message;
          } else if (response.message && typeof response.message === 'string') {
            errorMessage = response.message;
          } else if (typeof response.error === 'string') {
            errorMessage = response.error;
          }

          console.error("Extracted error message:", errorMessage);
          showToast(errorMessage, "error");
        }
      })
      .catch((error) => {
        console.error("Password update failed (catch):", error);
        let errorMessage = "Something went wrong";

        if (error?.response?.data?.message && typeof error.response.data.message === 'string') {
          errorMessage = error.response.data.message;
        } else if (error?.response?.data?.error?.message && typeof error.response.data.error.message === 'string') {
          errorMessage = error.response.data.error.message;
        } else if (error?.message && typeof error.message === 'string') {
          errorMessage = error.message;
        }

        showToast(errorMessage, "error");
      });
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(submit)}
        className="flex flex-col gap-5"
      >
        <Input
          name="oldPassword"
          label="Old Password"
          type={showOldPassword ? "text" : "password"}
          placeholder="Enter old password"
          required
          noCapitalize
          rightIcon={
            <FontAwesomeIcon icon={showOldPassword ? faEyeSlash : faEye} />
          }
          onRightIconClick={() =>
            setShowOldPassword(!showOldPassword)
          }
        />
        <Input
          name="newPassword"
          label="New Password"
          type={showNewPassword ? "text" : "password"}
          placeholder="Enter new password"
          required
          noCapitalize
          rightIcon={
            <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
          }
          onRightIconClick={() =>
            setShowNewPassword(!showNewPassword)
          }
        />

        <Input
          name="confirmPassword"
          label="Confirm Password"
          type={showConfirmPassword ? "text" : "password"}
          placeholder="Confirm new password"
          required
          noCapitalize
          rightIcon={
            <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
          }
          onRightIconClick={() =>
            setShowConfirmPassword(!showConfirmPassword)
          }
        />

        <div className="text-xs text-gray-600 -mt-3">
          <p className="font-semibold mb-1">Password must contain:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>At least 6 characters</li>
            <li>One uppercase letter</li>
            <li>One number</li>
            <li>One special character</li>
          </ul>
        </div>

        <div className="flex gap-4 justify-end">
          <PillButton type="submit" variant="secondary">
            Submit
          </PillButton>
          <PillButton
            type="button"
            onClick={onClose}
            variant="primary"
          >
            Cancel
          </PillButton>
        </div>
      </form>
    </FormProvider>
  );
};

export default UpdatePasswordForm;
